import React from "react"
import { useTranslation } from "react-i18next"

import { FlowDirectionTooltipContent } from "@/components/fields/flow-direction-tooltip-content"
import { JsonPreview } from "@/components/json-preview/json-preview"
import { useUserSettings } from "@/contexts"
import { useFormatDateRange } from "@/formatters"
import type { SelectableLanguageCode } from "@/models/i18n"
import type { BillPdfPreviewRow } from "@/modules/data/services/use-bill-pdf-upload-session"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { HStack } from "@/nzds/layout"
import { displayResourceCode } from "@/services/bill/bill-resource"
import { useFormatFlowDirection } from "@/utils/bill/format-flow-direction"
import { PERIOD_DATE_FORMAT } from "@/utils/date"
import { formatUsage } from "@/utils/format-usage/format-usage"
import { formatCost } from "@/utils/grid-columns/use-cost-column"
import { getDisplayValue } from "@/utils/value"

import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material"

interface BillPdfPreviewDetailDialogProps {
  onClose: () => void
  open: boolean
  row: BillPdfPreviewRow | null
}

interface BillPdfPreviewDetailExtraProps {
  errorMessage: string
  parsedJson: string
}

interface BillPdfPreviewDetailSummaryProps {
  row: BillPdfPreviewRow
}

const getParsedJsonFromRow = (row: BillPdfPreviewRow): string =>
  row.detail?.parsedJson?.trim() ?? ""

const getErrorMessageFromRow = (row: BillPdfPreviewRow): string =>
  row.status === "error" ? (row.errorMessage?.trim() ?? "") : ""

const BillPdfPreviewDetailSummary: React.FC<
  BillPdfPreviewDetailSummaryProps
> = ({ row }) => {
  const { i18n, t } = useTranslation("common")
  const { formatDateRange } = useFormatDateRange(PERIOD_DATE_FORMAT)
  const { formatFlowDirection } = useFormatFlowDirection()
  const { unitsSystem } = useUserSettings()

  return (
    <Stack sx={{ gap: 2 }}>
      <ReadonlyField
        label={t("fields.siteName.title")}
        value={getDisplayValue(row.siteName)}
      />
      <ReadonlyField
        label={t("fields.meterCode.title")}
        value={getDisplayValue(row.meterCode)}
      />
      <ReadonlyField
        label={t("fields.utilityProvider.title")}
        value={getDisplayValue(row.utilityProvider)}
      />
      <ReadonlyField
        label={t("fields.resource.title")}
        value={displayResourceCode(row.resourceType, (key) => t(key))}
      />
      <ReadonlyField
        label={t("fields.period.title")}
        value={formatDateRange(row.startDate, row.endDate)}
      />
      <ReadonlyField
        label={t("fields.flowDirection.title")}
        tooltipContent={<FlowDirectionTooltipContent />}
        value={formatFlowDirection(row.flowDirection)}
      />
      <ReadonlyField
        label={t("fields.usage.title")}
        value={formatUsage(
          row.usage,
          row.usageUnit,
          i18n.language as SelectableLanguageCode,
          unitsSystem
        )}
      />
      <ReadonlyField
        label={t("fields.cost.title")}
        value={formatCost(row.cost, row.currency, {
          localeCode: i18n.language as SelectableLanguageCode,
        })}
      />
    </Stack>
  )
}

const BillPdfPreviewDetailExtra: React.FC<BillPdfPreviewDetailExtraProps> = ({
  errorMessage,
  parsedJson,
}) => {
  const { t } = useTranslation("data")

  if (errorMessage !== "") {
    return (
      <Stack sx={{ gap: 1, minWidth: 0 }}>
        <Typography variant="subtitle2">
          {t("upload.billPdf.columnError")}
        </Typography>
        <Typography>{errorMessage}</Typography>
      </Stack>
    )
  }

  return (
    <Stack sx={{ gap: 1, minWidth: 0 }}>
      <Typography variant="subtitle2">
        {t("upload.billPdf.detail.parsedBill")}
      </Typography>
      <Box sx={{ maxHeight: 420, minHeight: 0, overflow: "auto" }}>
        <JsonPreview previewText={parsedJson} />
      </Box>
    </Stack>
  )
}

export const BillPdfPreviewDetailDialog: React.FC<
  BillPdfPreviewDetailDialogProps
> = ({ onClose, open, row }) => {
  const [displayedRow, setDisplayedRow] = React.useState(row)
  if (row != null && row !== displayedRow) {
    setDisplayedRow(row)
  }

  const title = displayedRow?.sourceFilename?.trim() ?? ""
  const parsedJson = displayedRow ? getParsedJsonFromRow(displayedRow) : ""
  const errorMessage = displayedRow ? getErrorMessageFromRow(displayedRow) : ""
  const hasExtra = errorMessage !== "" || parsedJson !== ""

  const handleExited = (): void => {
    if (row == null) {
      setDisplayedRow(null)
    }
  }

  return (
    <Dialog
      aria-labelledby="bill-pdf-preview-detail-title"
      data-testid="bill-pdf-preview-detail-dialog"
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      slotProps={{
        transition: {
          onExited: handleExited,
        },
      }}
    >
      <DialogTitle id="bill-pdf-preview-detail-title" onClose={onClose}>
        {title}
      </DialogTitle>
      {displayedRow ? (
        <DialogContent>
          <HStack
            sx={{
              alignItems: "stretch",
              gap: 3,
              pt: 1,
            }}
          >
            <Box sx={{ flex: "1 1 220px", maxWidth: 320 }}>
              <BillPdfPreviewDetailSummary row={displayedRow} />
            </Box>
            {hasExtra ? (
              <>
                <Divider flexItem orientation="vertical" />
                <Box sx={{ flex: "2 1 320px", minWidth: 0 }}>
                  <BillPdfPreviewDetailExtra
                    errorMessage={errorMessage}
                    parsedJson={parsedJson}
                  />
                </Box>
              </>
            ) : null}
          </HStack>
        </DialogContent>
      ) : null}
    </Dialog>
  )
}
